import React, {useMemo, useState, useEffect} from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Typography,
} from '@material-ui/core';
import {IFdFundingRequestCost} from 'constants/interfaces';

type Props = {
  options: IFdFundingRequestCost[];
  initialSelected?: string[];
  isLoading: boolean;
  onSubmit: (selectedIds: string[], notes: string) => void;
  onCancel?: () => void;
  submitLabel: string;
  cancelLabel?: string;
  readonly?: boolean;
};

export const FundingAskForm: React.FC<Props> = ({
  options,
  initialSelected = [],
  isLoading,
  onSubmit,
  onCancel,
  submitLabel,
  cancelLabel,
  readonly,
}) => {
  const [selected, setSelected] = useState<string[]>(initialSelected);
  const [notes, setNotes] = useState<string>('');

  useEffect(() => {
    setSelected(initialSelected);
  }, [initialSelected]);

  const toggle = (id: string) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const total = useMemo(
    () =>
      options
        .filter((o) => selected.includes(o.id))
        .reduce((s, x) => s + x.cost, 0),
    [options, selected],
  );

  return (
    <Paper
      elevation={0}
      style={{boxShadow: 'none', border: 'none', padding: 24}}
      className="generic-form-container funding-ask-form"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(selected, notes.trim());
        }}
      >
        <Grid container spacing={2}>
          {options.map((opt) => (
            <Grid item xs={12} key={opt.id}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={selected.includes(opt.id)}
                    onChange={() => toggle(opt.id)}
                    disabled={readonly || isLoading}
                  />
                }
                label={
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    width="100%"
                  >
                    <Typography>{opt.label}</Typography>
                    <Typography>R{opt.cost.toLocaleString()}</Typography>
                  </Box>
                }
              />
            </Grid>
          ))}

          <Grid item xs={12}>
            <Typography variant="subtitle2">Notes (optional)</Typography>
            <textarea
              style={{
                width: '100%',
                minHeight: 80,
                padding: 8,
                border: '1px solid #e7e5e4',
                borderRadius: 4,
                resize: 'vertical',
              }}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={readonly || isLoading}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <Typography
              align="right"
              style={{fontWeight: 700, color: '#013333'}}
            >
              TOTAL:&nbsp;R{total.toLocaleString()}
            </Typography>
          </Grid>

          {!readonly && (
            <>
              {cancelLabel && (
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    className="secondary-btn"
                    disabled={isLoading}
                    onClick={onCancel}
                  >
                    {cancelLabel}
                  </Button>
                </Grid>
              )}
              <Grid item xs={cancelLabel ? 6 : 12}>
                <Button
                  fullWidth
                  color="primary"
                  variant="contained"
                  type="submit"
                  disabled={!selected.length || isLoading}
                >
                  {isLoading ? (
                    <CircularProgress size={24} thickness={6} />
                  ) : (
                    submitLabel
                  )}
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </form>
    </Paper>
  );
};
